import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Loader,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { AlertCircle, ChevronLeft, ChevronRight, Globe } from 'react-feather';
import { schedulingTokens as tokens } from './schedulingTokens';
import classes from './SlotPicker.module.css';
import type {
  AvailabilitySlot,
  MonthAvailability,
} from './useMonthAvailability';
import {
  DAY_HEADERS,
  MONTH_NAMES,
  formatDateKey,
  formatLongDate,
  formatMonthKey,
  formatShortDate,
  formatTimeSlot,
  getMonthDays,
} from './slotPickerUtils';
import { getZoneDisplayLabel } from '../../config/timezones';

export interface SlotPickerProps<T extends AvailabilitySlot> {
  availability: MonthAvailability<T>;
  /** The viewer's IANA timezone. */
  timezone: string;
  selectedDate: string | null;
  onSelectDate: (dateKey: string) => void;
  selectedSlot: T | null;
  onSelectSlot: (slot: T) => void;
  /**
   * Called when the viewer moves to a month that does not hold the selected
   * date. The parent clears the selected date and slot.
   */
  onClearSelection?: () => void;
  /** Called when the viewer confirms the selected slot. */
  onNext: () => void;
  /** Content under the timezone line, for example an error or an empty message. */
  status?: ReactNode;
  /** Content above the calendar, for a notice that explains the whole grid. */
  topStatus?: ReactNode;
  /**
   * "panels" gives the calendar and the slot list as two side-by-side panels
   * (the parent supplies the row). "stacked" puts the slot list under the
   * calendar, for narrow screens.
   */
  layout?: 'panels' | 'stacked';
}

/** A month grid of days with free slots, and the list of free times for the selected day. */
export function SlotPicker<T extends AvailabilitySlot>({
  availability,
  timezone,
  selectedDate,
  onSelectDate,
  selectedSlot,
  onSelectSlot,
  onClearSelection,
  onNext,
  status,
  topStatus,
  layout = 'panels',
}: SlotPickerProps<T>) {
  const {
    today,
    viewYear,
    viewMonth,
    prevMonth,
    nextMonth,
    isPrevDisabled,
    slotsByDate,
    isLoading,
    partialFailure,
    reload,
  } = availability;
  const stacked = layout === 'stacked';
  const dayHeight = stacked ? 44 : 40;

  // Name the timezone as it is in the visible month: today for the current
  // month, else the middle of the month.
  const timezoneLabel = useMemo(() => {
    const isCurrentMonth =
      viewYear === today.getFullYear() && viewMonth === today.getMonth();
    const at = isCurrentMonth ? today : new Date(viewYear, viewMonth, 15, 12);
    return getZoneDisplayLabel(timezone, at);
  }, [timezone, today, viewYear, viewMonth]);
  const monthDays = useMemo(
    () => getMonthDays(viewYear, viewMonth),
    [viewYear, viewMonth]
  );
  const todayDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()),
    [today]
  );

  // Do not keep a selected day from another month. Its times and its Next
  // button would sit next to a grid that does not show that day.
  const clearSelectionRef = useRef(onClearSelection);
  clearSelectionRef.current = onClearSelection;
  useEffect(() => {
    if (
      selectedDate &&
      !selectedDate.startsWith(`${formatMonthKey(viewYear, viewMonth)}-`)
    ) {
      clearSelectionRef.current?.();
    }
  }, [selectedDate, viewYear, viewMonth]);

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    return [...(slotsByDate.get(selectedDate) ?? [])].sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }, [selectedDate, slotsByDate]);

  // On narrow screens the slot list sits below the fold. Scroll it into view
  // when the viewer picks a day.
  const slotListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stacked && selectedDate) {
      slotListRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [stacked, selectedDate]);

  // Also scroll the Next button into view when the viewer picks a time.
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (stacked && selectedSlot) {
      nextButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [stacked, selectedSlot]);

  const selectedDayLabel = selectedDate ? formatLongDate(selectedDate) : null;

  const calendar = (
    <>
      {topStatus}

      {/* Month nav */}
      <Group justify="space-between" mb="md">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={prevMonth}
          disabled={isPrevDisabled}
          size={stacked ? 44 : 'md'}
          className={classes.monthNav}
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </ActionIcon>
        <Text fw={600} size="md" aria-live="polite">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={nextMonth}
          size={stacked ? 44 : 'md'}
          className={classes.monthNav}
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </ActionIcon>
      </Group>

      {/* Some windows failed after a retry: the month shows fewer times than
        it has. Say so above the grid, where the missing days are. */}
      {!isLoading && partialFailure && (
        <Alert
          color="warningAmber.5"
          variant="light"
          icon={<AlertCircle size={16} />}
          mb="md"
          styles={{ message: { color: tokens.textPrimary } }}
        >
          <Text size="sm">
            Some days may be missing. We could not load all open times.
          </Text>
          <Button
            size="xs"
            variant="light"
            color="medleyGreen"
            mt="xs"
            onClick={reload}
          >
            Load again
          </Button>
        </Alert>
      )}

      {/* Day headers */}
      <Group gap={0} mb="xs" aria-hidden>
        {DAY_HEADERS.map(d => (
          <Text
            key={d}
            size="xs"
            c="dimmed"
            ta="center"
            fw={500}
            style={{ width: 'calc(100% / 7)' }}
          >
            {d}
          </Text>
        ))}
      </Group>

      {/* Calendar grid */}
      <Box
        className={classes.grid}
        data-loading={isLoading ? 'true' : 'false'}
        aria-busy={isLoading}
        style={
          {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 2,
            '--slot-day-size': `${dayHeight}px`,
          } as React.CSSProperties
        }
      >
        {monthDays.map((day, idx) => {
          if (day === null) {
            return <Box key={`empty-${idx}`} style={{ height: dayHeight }} />;
          }

          const dateKey = formatDateKey(viewYear, viewMonth, day);
          const available = (slotsByDate.get(dateKey)?.length ?? 0) > 0;
          const past = new Date(viewYear, viewMonth, day) < todayDate;
          const isTodayDay =
            viewYear === today.getFullYear() &&
            viewMonth === today.getMonth() &&
            day === today.getDate();
          const isSelected = selectedDate === dateKey;

          const state = isSelected
            ? 'selected'
            : past
              ? 'past'
              : available
                ? 'available'
                : 'unavailable';

          return (
            <UnstyledButton
              key={dateKey}
              className={classes.dayCell}
              data-state={state}
              onClick={() => {
                if (past || !available) return;
                onSelectDate(dateKey);
              }}
              disabled={past || !available}
              aria-label={`${formatLongDate(dateKey)}${available && !past ? ', times available' : ''}`}
              aria-pressed={isSelected}
            >
              <span className={classes.dayCircle}>
                {day}
                {isTodayDay && !isSelected && (
                  <span className={classes.todayDot} aria-hidden />
                )}
              </span>
            </UnstyledButton>
          );
        })}
      </Box>

      {/* Timezone label */}
      <Group gap={6} mt="md">
        <Globe size={14} style={{ color: tokens.textTertiary }} />
        <Text size="xs" c="dimmed">
          {timezoneLabel}
        </Text>
      </Group>

      {/* Loading indicator */}
      {isLoading && (
        <Group gap={8} justify="center" mt="md" role="status">
          <Loader size="sm" color="medleyGreen" />
          <Text size="xs" style={{ color: tokens.textTertiary }}>
            Loading open times...
          </Text>
        </Group>
      )}

      {status}
    </>
  );

  if (stacked) {
    return (
      <Stack gap="lg">
        <Box>
          <Text fw={600} size="md" mb="lg">
            Pick a date and time
          </Text>
          {calendar}
        </Box>

        {selectedDate && (
          <Box ref={slotListRef} style={{ scrollMarginTop: 16 }}>
            <Text fw={600} size="sm" mb="md">
              {selectedDayLabel}
            </Text>
            {selectedDateSlots.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No available times
              </Text>
            ) : (
              <SimpleGrid
                cols={{ base: 2, xs: 3 }}
                spacing={8}
                verticalSpacing={8}
              >
                {selectedDateSlots.map(slot => {
                  const isSlotSelected =
                    selectedSlot?.start_time === slot.start_time;
                  return (
                    <UnstyledButton
                      key={slot.start_time}
                      onClick={() => onSelectSlot(slot)}
                      aria-pressed={isSlotSelected}
                      style={{
                        padding: '12px 0',
                        textAlign: 'center',
                        borderRadius: 6,
                        border: isSlotSelected
                          ? `2px solid ${tokens.primary}`
                          : `1px solid ${tokens.borderDark}`,
                        backgroundColor: isSlotSelected
                          ? tokens.primaryTint
                          : tokens.surface,
                        fontWeight: isSlotSelected ? 600 : 400,
                        fontSize: 14,
                        color: tokens.textPrimary,
                        cursor: 'pointer',
                      }}
                    >
                      {formatTimeSlot(slot.start_time, timezone)}
                    </UnstyledButton>
                  );
                })}
              </SimpleGrid>
            )}
            {selectedSlot && (
              <Button
                ref={nextButtonRef}
                fullWidth
                size="md"
                mt="md"
                onClick={onNext}
                style={{ backgroundColor: tokens.primary, border: 'none' }}
              >
                Next
              </Button>
            )}
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <>
      {/* ===== CENTER PANEL: Calendar ===== */}
      <Box p="lg" style={{ flex: '1 1 auto', minWidth: 300 }}>
        <Text fw={600} size="md" mb="lg">
          Pick a date and time
        </Text>
        {calendar}
      </Box>

      {/* ===== RIGHT PANEL: Time Slots ===== */}
      {/* The column is always there, so the layout does not move when the
        viewer picks the first day. */}
      <Box
        p="lg"
        style={{
          width: 232,
          minWidth: 232,
          borderLeft: `1px solid ${tokens.border}`,
        }}
      >
        {!selectedDate ? (
          <Text size="13px" pt={2} style={{ color: tokens.textTertiary }}>
            Pick a day to see times
          </Text>
        ) : (
          <>
            <Text fw={600} size="sm" mb="md">
              {formatShortDate(selectedDate)}
            </Text>

            <ScrollArea h={420} type="auto" offsetScrollbars>
              <Stack gap={8}>
                {selectedDateSlots.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    No available times
                  </Text>
                ) : (
                  selectedDateSlots.map(slot => {
                    const isSlotSelected =
                      selectedSlot?.start_time === slot.start_time;
                    const timeLabel = formatTimeSlot(slot.start_time, timezone);

                    return (
                      <Group key={slot.start_time} gap={4} wrap="nowrap">
                        <UnstyledButton
                          onClick={() => onSelectSlot(slot)}
                          aria-pressed={isSlotSelected}
                          style={{
                            flex: 1,
                            padding: '10px 0',
                            textAlign: 'center',
                            borderRadius: 6,
                            border: isSlotSelected
                              ? `2px solid ${tokens.primary}`
                              : `1px solid ${tokens.borderDark}`,
                            backgroundColor: isSlotSelected
                              ? tokens.primaryTint
                              : tokens.surface,
                            fontWeight: isSlotSelected ? 600 : 400,
                            fontSize: 14,
                            color: tokens.textPrimary,
                            cursor: 'pointer',
                          }}
                        >
                          {timeLabel}
                        </UnstyledButton>
                        {isSlotSelected && (
                          <Button
                            size="xs"
                            onClick={onNext}
                            style={{
                              backgroundColor: tokens.primary,
                              border: 'none',
                              flexShrink: 0,
                            }}
                          >
                            Next
                          </Button>
                        )}
                      </Group>
                    );
                  })
                )}
              </Stack>
            </ScrollArea>
          </>
        )}
      </Box>
    </>
  );
}
